import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Dialog from 'src/components/dialog/Dialog';

export type SendStatus = 'pending' | 'sending' | 'success' | 'error';

export type SendProgressItem = {
    leadUuid: string;
    leadName: string;
    status: SendStatus;
    error?: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
    items: SendProgressItem[];
    isDone: boolean;
};

export default function BulkSendProgressModal({ open, onClose, items, isDone }: Props) {
    return (
        <Dialog open={open}>
            <DialogTitle>
                {isDone ? 'Messages sent' : 'Sending messages…'}
            </DialogTitle>
            <DialogContent sx={{ overflowY: 'auto', maxHeight: 400 }}>
                <List dense disablePadding>
                    {items.map((item) => (
                        <ListItem
                            key={item.leadUuid}
                            disableGutters
                            secondaryAction={item.status === 'error' ? (
                                <Tooltip title={item.error ?? 'Failed to send'} placement="left">
                                    <IconButton size="small" tabIndex={-1} aria-label="Send error details">
                                        <InfoOutlinedIcon fontSize="small" color="error" />
                                    </IconButton>
                                </Tooltip>
                            ) : undefined}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                {item.status === 'pending' && <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                                {item.status === 'sending' && <CircularProgress size={18} />}
                                {item.status === 'success' && <CheckCircleOutlineIcon fontSize="small" color="success" />}
                                {item.status === 'error' && <CheckCircleOutlineIcon fontSize="small" sx={{ opacity: 0 }} />}
                            </ListItemIcon>
                            <ListItemText primary={item.leadName || item.leadUuid} />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose} disabled={!isDone}>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}
